import { AfterViewInit, Component, OnDestroy, TemplateRef, ViewChild } from '@angular/core';
import { MatSelectChange } from '@angular/material/select';
import { MatTableDataSource } from '@angular/material/table';
import { RootState } from '@next/core-lib/app';
import { AuthorizationSelectors } from '@next/core-lib/authorization';
import { LocaleService } from '@next/core-lib/i18n';
import { TableColumnTypes, TableConfig } from '@next/core-lib/table';
import { Store, select } from '@ngrx/store';
import { Observable, Subscription } from 'rxjs';

import { map, withLatestFrom } from 'rxjs/operators';

import { COUNTRIES } from '../../../../assets/data/countries';
import { ReferenceScheme } from '../../models/reference-scheme.model';
import * as referenceSchemeActions from '../../store/reference-scheme.actions';
import { getReferenceSchemes, getSelectedCountry } from '../../store/reference-scheme.selectors';

@Component({
  selector: 'app-reference-scheme-overview',
  templateUrl: './reference-scheme-overview.component.html',
  styleUrls: ['./reference-scheme-overview.component.scss'],
  standalone: false,
})
export class ReferenceSchemeOverviewComponent implements AfterViewInit, OnDestroy {
  /* SELECTORS & DATA */
  selectedCountry$: Observable<string> = this.store.select(getSelectedCountry);

  referenceSchemes$ = this.store.select(getReferenceSchemes).pipe(
    withLatestFrom(this.selectedCountry$),
    map(([referenceSchemes, selectedCountry]) =>
      referenceSchemes.filter((referenceScheme) => referenceScheme?.country === selectedCountry),
    ),
  );

  /* INDICATORS */
  isAdvisor = false;

  /* SUBSCRIPTIONS */
  subAuthorization: Subscription;
  subReferenceSchemes: Subscription;

  /* TEMPLATES */
  @ViewChild('columnNameTemplate') columnNameTemplate: TemplateRef<unknown>;
  @ViewChild('columnActiveTemplate') columnActiveTemplate: TemplateRef<unknown>;

  /* TABLE CONFIG */
  tableConfig: TableConfig = {
    dataSource: new MatTableDataSource([] as ReferenceScheme[]),
    selectColumn: false,
    pagination: false,
    actionColumn: true,
    disableCards: true,
    footer: false,
    scrolling: true,
    columns: [
      {
        id: 'name',
        type: TableColumnTypes.TEMPLATE,
        label: this.translate('table-header.name'),
      },
      {
        id: 'created',
        type: TableColumnTypes.DATE,
        label: this.translate('table-header.created'),
      },
      {
        id: 'updated',
        type: TableColumnTypes.DATE,
        label: this.translate('table-header.updated'),
      },
      {
        id: 'updatedAdvisorName',
        type: TableColumnTypes.STRING,
        label: this.translate('table-header.updatedAdvisorName'),
      },
      {
        id: 'isActive',
        type: TableColumnTypes.TEMPLATE,
        label: this.translate('table-header.isActive'),
      },
    ],
    actions: [
      {
        id: 'edit',
        label: this.translate('buttons.edit'),
        callback: (referenceScheme: ReferenceScheme) => {
          if (this.isAdvisor) {
            this.store.dispatch(
              referenceSchemeActions.editReferenceSchemeNavigation({
                referenceScheme,
              }),
            );
          }
        },
        icon: 'edit',
        disabled: () => !this.isAdvisor,
      },
      {
        id: 'delete',
        label: this.translate('buttons.delete'),
        callback: (referenceScheme: ReferenceScheme) => {
          if (this.isAdvisor) {
            this.store.dispatch(
              referenceSchemeActions.deleteReferenceSchemeConfirm({
                referenceScheme,
              }),
            );
          }
        },
        icon: 'delete',
        disabled: () => !this.isAdvisor,
      },
    ],
  };

  countries: Array<{ id: string; value: string }> = COUNTRIES.sort((a, b) =>
    a.value < b.value ? -1 : 1,
  );

  constructor(
    private readonly localeService: LocaleService,
    private readonly store: Store<RootState>,
  ) {
    // Set authorization roles indicators.
    this.subAuthorization = this.store
      .pipe(select(AuthorizationSelectors.hasAtLeastAdvisorRole))
      .subscribe((hasAtLeastAdvisorRole: boolean) => {
        this.isAdvisor = hasAtLeastAdvisorRole;
      });

    // Get reference-schemes from state.
    this.subReferenceSchemes = this.referenceSchemes$.subscribe((referenceSchemes) => {
      if (
        referenceSchemes !== null &&
        referenceSchemes !== undefined &&
        referenceSchemes.length > 0
      ) {
        this.tableConfig.dataSource.data = referenceSchemes;
      } else {
        this.tableConfig.dataSource.data = [];
      }
    });
  }

  ngAfterViewInit() {
    // Set isActive column template.
    setTimeout(() => {
      const newConfig = { ...this.tableConfig };
      const columnName = newConfig.columns.find((c) => c.id === 'name');
      const columnActive = newConfig.columns.find((c) => c.id === 'isActive');

      columnName.template = this.columnNameTemplate;
      columnActive.template = this.columnActiveTemplate;
      this.tableConfig = newConfig;
    }, 50);
  }

  translate(key: string): string {
    return this.localeService.translate(`reference-scheme.overview.${key}`);
  }

  onButtonCreate() {
    if (this.isAdvisor) {
      this.store.dispatch(referenceSchemeActions.createReferenceSchemeNavigation());
    }
  }

  onToggleActive(referenceScheme: ReferenceScheme) {
    if (this.isAdvisor) {
      const lReferenceScheme = { ...referenceScheme };
      lReferenceScheme.isActive = !referenceScheme.isActive;
      this.store.dispatch(
        referenceSchemeActions.upsertReferenceSchemeRequest({ referenceScheme: lReferenceScheme }),
      );
    }
  }

  onViewReferenceScheme(referenceScheme: ReferenceScheme) {
    this.store.dispatch(
      referenceSchemeActions.viewReferenceSchemeNavigation({
        referenceScheme: { ...referenceScheme },
      }),
    );
  }

  ngOnDestroy(): void {
    if (this.subAuthorization) {
      this.subAuthorization.unsubscribe();
    }

    if (this.subReferenceSchemes) {
      this.subReferenceSchemes.unsubscribe();
    }
  }

  changeCountrySelection(selection: MatSelectChange) {
    this.store.dispatch(
      referenceSchemeActions.setSelectedCountryRequest({ id: selection.value as string }),
    );
  }
}
